import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import LoginForm from './LoginForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import ResetPasswordForm from './ResetPasswordForm';
import PropTypes from 'prop-types';

const AuthPage = ({ setToken }) => {
  const { darkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useParams();
  
  // Determine the current form to show based on URL
  const getCurrentForm = useCallback(() => {
    if (location.pathname.includes('/reset-password')) return 'reset';
    if (location.pathname.includes('/forgot-password')) return 'forgot';
    if (location.pathname.includes('/login')) return 'login';
    if (location.pathname.includes('/register')) return 'register';
    return 'login'; // default to login
  }, [location.pathname]);

  const [currentForm, setCurrentForm] = useState(getCurrentForm());

  // Update form when URL changes
  useEffect(() => {
    setCurrentForm(getCurrentForm());
  }, [getCurrentForm]);

  const handleFormSwitch = (form) => {
    if (form === 'forgot-password') {
      navigate('/forgot-password');
    } else if (form === 'login') {
      navigate('/login');
    } else if (form === 'register') {
      navigate('/register');
    } else if (form === 'reset-password') {
      navigate('/reset-password');
    }
  };

  const renderForm = () => {
    switch (currentForm) {
      case 'forgot':
        return <ForgotPasswordForm onBackToLogin={() => handleFormSwitch('login')} darkMode={darkMode} />;
      case 'reset':
        return <ResetPasswordForm token={token} onBackToLogin={() => handleFormSwitch('login')} darkMode={darkMode} />;
      default:
        return (
          <LoginForm 
            onForgotPassword={() => handleFormSwitch('forgot-password')} 
            setToken={setToken} 
            darkMode={darkMode}
          />
        );
    }
  };

  return (
    <div className={`relative min-h-screen flex items-center justify-center px-4 lg:px-0 overflow-hidden -mt-8 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50'}`}>
      {/* Background animation */}
      <motion.div
        className="absolute inset-0 -z-10 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className={`absolute inset-0 ${darkMode ? 'bg-gray-900' : 'bg-white'}`} />
      </motion.div>

      <div className={`w-full rounded-2xl p-2 max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-8 items-center ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
        {/* Left side - Image */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="hidden md:block rounded-xl overflow-hidden"
        >
          <div className="relative h-[460px]">
            <img 
              src="https://lh3.googleusercontent.com/d/1XOc5odpaSEmxq_COfFNW91FjeHB48PWK" 
              alt="Authentication" 
              className="absolute inset-0 w-full h-full object-cover" 
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent p-8 flex flex-col justify-end">
              <motion.div 
                initial={{ y: 10, opacity: 0 }} 
                animate={{ y: 0, opacity: 1 }} 
                transition={{ delay: 0.1 }} 
                className="max-w-sm"
              >
                <h2 className="text-white text-2xl font-bold">
                  {currentForm === 'login' ? 'Welcome back 👋' : 
                   currentForm === 'forgot' ? 'Forgot your password?' : 
                   'Reset your password'}
                </h2>
                <p className="text-white/80 mt-1 text-sm">
                  {currentForm === 'login' ? 'Sign in to manage your projects, messages, and profile.' :
                   currentForm === 'forgot' ? 'Enter your email and we\'ll send you a link to reset your password.' :
                   'Please enter your new password.'}
                </p>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Right side - Form */}
        <motion.div
          key={currentForm}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className={`w-full p-8 space-y-8 rounded-xl ${darkMode ? 'bg-gray-800/70' : 'bg-white/80 backdrop-blur-sm'}`}
        >
          <AnimatePresence mode="wait">
            {renderForm()}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

AuthPage.propTypes = {
  setToken: PropTypes.func
};

export default AuthPage;